// ---------------------------------------------------------------------------
// CampaignsTable (OPUS-D04)
// ---------------------------------------------------------------------------
// Presentational table for the campaigns dashboard. Consumes the rows loaded
// by `useCampaignsList` and renders them in a clean, brand-aligned layout.
//
// The table currently uses a hand-rolled column-def pipeline that mirrors the
// shape of `@tanstack/react-table`. The dash scaffold has not yet declared
// react-table as a dep (agents cannot bump package.json), so we keep the
// mechanics compatible so a future migration is a drop-in.
// ---------------------------------------------------------------------------

import type { JSX } from "react";
import {
	type CampaignRow,
	type CampaignState,
	STATE_BADGE_TONE,
	STATE_LABEL,
	formatCount,
	formatTimestamp,
} from "./api";

export interface CampaignsTableProps {
	rows: CampaignRow[];
	isLoading: boolean;
	isError: boolean;
	error?: Error;
	onRetry?: () => void;
}

interface Column {
	key: string;
	header: string;
	align?: "left" | "right";
	width?: string;
	render: (row: CampaignRow) => JSX.Element | string;
}

// ---- badge ----------------------------------------------------------------

const BADGE_CLASSES: Record<
	NonNullable<ReturnType<typeof stateTone>>,
	string
> = {
	emerald:
		"inline-flex items-center rounded-[6px] border border-[color:var(--color-brand-emerald)]/30 bg-[color:var(--color-brand-emerald)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-brand-emerald)]",
	sky: "inline-flex items-center rounded-[6px] border border-[color:var(--color-brand-sky)]/30 bg-[color:var(--color-brand-sky)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-brand-sky)]",
	amber:
		"inline-flex items-center rounded-[6px] border border-[color:var(--color-warning)]/30 bg-[color:var(--color-warning)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-warning)]",
	zinc: "inline-flex items-center rounded-[6px] border border-[color:var(--color-border-strong)] bg-[color:var(--color-bg-alt)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]",
};

function stateTone(s: CampaignState) {
	return STATE_BADGE_TONE[s];
}

function StateBadge({ state }: { state: CampaignState }) {
	const tone = stateTone(state);
	return (
		<span className={BADGE_CLASSES[tone]} data-testid={`campaign-state-${state}`}>
			{STATE_LABEL[state]}
		</span>
	);
}

// ---- column defs ----------------------------------------------------------

const COLUMNS: Column[] = [
	{
		key: "id",
		header: "ID",
		width: "w-[14%]",
		render: (row) => (
			<span className="font-mono text-xs text-[color:var(--color-text-muted)]">
				{row.id.slice(0, 10)}
			</span>
		),
	},
	{
		key: "name",
		header: "Campaign",
		width: "w-[32%]",
		render: (row) => (
			<span className="font-medium text-[color:var(--color-text)]">
				{row.name}
			</span>
		),
	},
	{
		key: "state",
		header: "Status",
		width: "w-[12%]",
		render: (row) => <StateBadge state={row.state} />,
	},
	{
		key: "clicks_count",
		header: "Clicks",
		align: "right",
		width: "w-[12%]",
		render: (row) => (
			<span className="tabular-nums">{formatCount(row.clicks_count)}</span>
		),
	},
	{
		key: "conversions_count",
		header: "Conversions",
		align: "right",
		width: "w-[14%]",
		render: (row) => (
			<span className="tabular-nums">{formatCount(row.conversions_count)}</span>
		),
	},
	{
		key: "updated_at",
		header: "Updated",
		align: "right",
		width: "w-[16%]",
		render: (row) => (
			<span className="tabular-nums text-xs text-[color:var(--color-text-muted)]">
				{formatTimestamp(row.updated_at)}
			</span>
		),
	},
];

// ---- shells ---------------------------------------------------------------

function Shell({ children }: { children: JSX.Element | JSX.Element[] }) {
	return (
		<div
			className="overflow-hidden rounded-[8px] border bg-[color:var(--color-surface)] shadow-[var(--shadow-brand-subtle)]"
			style={{ borderColor: "var(--color-border)" }}
		>
			{children}
		</div>
	);
}

function HeaderRow() {
	return (
		<div
			className="grid grid-cols-12 gap-2 border-b bg-[color:var(--color-bg-alt)] px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
			style={{ borderColor: "var(--color-border)" }}
		>
			{COLUMNS.map((col) => (
				<div
					key={col.key}
					className={`${col.width ?? ""} ${
						col.align === "right" ? "text-right" : "text-left"
					}`}
				>
					{col.header}
				</div>
			))}
		</div>
	);
}

function Row({ row }: { row: CampaignRow }) {
	// NOTE: we intentionally use a plain <a> here rather than TanStack Router's
	// typed <Link>. The detail route `/campaigns/$campaignId` has not been
	// registered yet (lands in a follow-up agent), so a typed Link would fail
	// the strict `Register` interface. A raw href keeps the click behaviour
	// correct and will still be intercepted once the router picks it up.
	return (
		<a
			href={`/campaigns/${row.id}`}
			className="grid grid-cols-12 items-center gap-2 border-b px-4 py-3 text-sm transition-colors hover:bg-[color:var(--color-bg-alt)]"
			style={{ borderColor: "var(--color-border)" }}
			data-testid={`campaign-row-${row.id}`}
		>
			{COLUMNS.map((col) => (
				<div
					key={col.key}
					className={`${col.width ?? ""} ${
						col.align === "right" ? "text-right" : "text-left"
					} truncate`}
				>
					{col.render(row)}
				</div>
			))}
		</a>
	);
}

function EmptyState({ label }: { label: string }) {
	return (
		<div className="px-4 py-10 text-center text-sm text-[color:var(--color-text-muted)]">
			{label}
		</div>
	);
}

function LoadingSkeleton() {
	return (
		<div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are position-anchored
					key={i}
					className="grid grid-cols-12 gap-2 border-b px-4 py-3"
					style={{ borderColor: "var(--color-border)" }}
				>
					{COLUMNS.map((col) => (
						<div
							key={col.key}
							className={`${col.width ?? ""} h-4 animate-pulse rounded-[4px] bg-[color:var(--color-border)]`}
						/>
					))}
				</div>
			))}
		</div>
	);
}

function ErrorState({ error, onRetry }: { error?: Error; onRetry?: () => void }) {
	return (
		<div className="space-y-3 px-4 py-10 text-center">
			<p className="text-sm font-medium text-[color:var(--color-danger)]">
				Could not load campaigns.
			</p>
			{error ? (
				<p className="text-xs text-[color:var(--color-text-muted)]">
					{error.message}
				</p>
			) : null}
			{onRetry ? (
				<button
					type="button"
					onClick={onRetry}
					className="inline-flex items-center rounded-[6px] border px-3 py-1.5 text-xs font-medium text-[color:var(--color-text)] transition-colors hover:bg-[color:var(--color-bg-alt)]"
					style={{ borderColor: "var(--color-border-strong)" }}
				>
					Retry
				</button>
			) : null}
		</div>
	);
}

// ---- main component -------------------------------------------------------

export function CampaignsTable({
	rows,
	isLoading,
	isError,
	error,
	onRetry,
}: CampaignsTableProps) {
	let body: JSX.Element;
	if (isLoading) {
		body = <LoadingSkeleton />;
	} else if (isError) {
		body = <ErrorState error={error} onRetry={onRetry} />;
	} else if (rows.length === 0) {
		body = (
			<EmptyState label="No campaigns yet. Your running campaigns will show up here." />
		);
	} else {
		body = (
			<div>
				{rows.map((row) => (
					<Row key={row.id} row={row} />
				))}
			</div>
		);
	}

	return (
		<Shell>
			<HeaderRow />
			{body}
		</Shell>
	);
}

export default CampaignsTable;
